import { Paper, Title, Text, TextInput, PasswordInput, Button, Flex, SimpleGrid, Box, Checkbox, Anchor, PinInput } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { IconBuilding, IconMail, IconLock, IconBriefcase, IconWorld } from "@tabler/icons-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import trpc, { saveToken } from "../../shared/utils/trpc";
import { handleTrpcError } from "../../shared/utils/trpc/handletrpcError.util";
import { successNotification } from "../../shared/utils/successNotification";
import { SignupFlowLayout } from "./SignupFlowLayout";
import "react-international-phone/style.css";
import { PhoneInput } from "react-international-phone";
import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";
import { useUser } from "@/context/UserContext";
import {
  clearSignupBasicDetailsDraft,
  clearSignupOtpState,
  clearSignupWebsitePrefill,
  readSignupBasicDetailsDraft,
  readSignupOtpState,
  writeSignupBasicDetailsDraft,
  writeSignupOtpState,
  writeSignupWebsitePrefill,
} from "./signupFlowStorage";

const TERMS_URL = "https://gtsetu.com/terms/";
const PRIVACY_URL = "https://gtsetu.com/privacy/";
const NAVY = "#04305c";

function formatOtpCooldown(seconds: number) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

function SignupPage() {
  const navigate = useNavigate();
  const { refetchUser } = useUser();
  const isSmUp = useMediaQuery("(min-width: 36em)") ?? false;

  const [activeStep, setActiveStep] = useState<0 | 1>(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [otpCooldown, setOtpCooldown] = useState(0);

  useEffect(() => {
    const token = Cookies.get("token");

    if (!token) return;

    try {
      const decoded = jwtDecode<{ role: string; exp: number }>(token);

      if (decoded.exp > Date.now() / 1000) {
        if (decoded.role === "ADMIN") {
          navigate("/admin/companies", { replace: true });
        } else {
          navigate("/profile", { replace: true });
        }
      } else {
        Cookies.remove("token");
      }
    } catch {
      Cookies.remove("token");
    }
  }, []);

  const [formData, setFormData] = useState({
    companyName: "",
    name: "",
    designation: "",
    websiteUrl: "",
    email: "",
    phoneNumber: "",
    phoneCountryCode: "",
    password: "",
    confirmPassword: "",
    acceptedTermsAndPrivacy: false,
  });

  useEffect(() => {
    const savedDraft = readSignupBasicDetailsDraft();
    if (savedDraft) {
      setFormData((prev) => ({ ...prev, ...savedDraft }));
    }
    const savedOtp = readSignupOtpState();
    if (
      savedOtp?.verificationPending &&
      savedOtp?.email &&
      savedDraft?.email &&
      savedOtp.email.toLowerCase() === savedDraft.email.toLowerCase()
    ) {
      setOtpCode(savedOtp.otp ?? "");
      setActiveStep(1);
    }
  }, []);

  useEffect(() => {
    writeSignupBasicDetailsDraft({
      companyName: formData.companyName,
      name: formData.name,
      designation: formData.designation,
      email: formData.email,
      phoneNumber: formData.phoneNumber,
      phoneCountryCode: formData.phoneCountryCode,
      websiteUrl: formData.websiteUrl,
      acceptedTermsAndPrivacy: formData.acceptedTermsAndPrivacy,
    });
  }, [formData]);

  useEffect(() => {
    if (activeStep === 1 && formData.email.trim()) {
      writeSignupOtpState({
        email: formData.email.trim(),
        otp: otpCode,
        verificationPending: true,
      });
    } else {
      clearSignupOtpState();
    }
  }, [activeStep, formData.email, otpCode]);

  useEffect(() => {
    if (otpCooldown <= 0) return;
    const timer = window.setInterval(() => {
      setOtpCooldown((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [otpCooldown]);

  const PERSONAL_EMAIL_DOMAINS = [
    "gmail.com",
    "yahoo.com",
    "yahoo.in",
    "yahoo.co.uk",
    "icloud.com",
    "me.com",
    "mac.com",
    "hotmail.com",
    "hotmail.in",
    "hotmail.co.uk",
    "outlook.com",
    "outlook.in",
    "live.com",
    "live.in",
    "msn.com",
    "aol.com",
    "protonmail.com",
    "proton.me",
    "zoho.com",
    "yandex.com",
    "yandex.ru",
    "mail.com",
    "gmx.com",
    "gmx.net",
    "rediffmail.com",
    "inbox.com",
    "fastmail.com",
    "tutanota.com",
  ];
  const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const isBusinessEmail = (email: string) => {
    const domain = email.split("@")[1]?.toLowerCase();
    return domain ? !PERSONAL_EMAIL_DOMAINS.includes(domain) : true;
  };
  const isPasswordValid = formData.password.length >= 8;
  const doPasswordsMatch = formData.confirmPassword === "" || formData.password === formData.confirmPassword;
  const isWebsiteValid = useMemo(() => {
    const value = formData.websiteUrl.trim();
    if (!value) return true;
    return /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/.*)?$/i.test(value);
  }, [formData.websiteUrl]);

  const isStep1Valid =
    formData.name.trim() !== "" &&
    formData.companyName.trim() !== "" &&
    formData.designation.trim() !== "" &&
    isWebsiteValid &&
    formData.phoneNumber.trim() !== "" &&
    isValidEmail(formData.email) &&
    isBusinessEmail(formData.email) &&
    isPasswordValid &&
    formData.password === formData.confirmPassword &&
    formData.acceptedTermsAndPrivacy;

  const handleStep1Submit = async () => {
    if (!isStep1Valid) return;

    setLoading(true);
    try {
      await trpc.auth.registerSignupStep1.mutate({
        email: formData.email,
        password: formData.password,
        userName: formData.name,
        companyName: formData.companyName,
        userDesignation: formData.designation,
        websiteUrl: formData.websiteUrl.trim(),
        userPhoneNumber: `${formData.phoneCountryCode}${formData.phoneNumber}`,
        acceptedTermsAndPrivacy: true,
      });

      successNotification({
        title: "Verification code sent",
        message: "Enter the 6-digit code sent to your email to continue.",
      });
      setOtpCode("");
      setOtpCooldown(60);
      writeSignupOtpState({
        email: formData.email.trim(),
        otp: "",
        verificationPending: true,
      });
      setActiveStep(1);
    } catch (err) {
      handleTrpcError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (otpCode.trim().length !== 6) return;

    setVerifyingOtp(true);
    try {
      const verificationResult = await trpc.auth.verifyEmail.mutate({
        email: formData.email.trim(),
        otp: otpCode.trim(),
      });

      const directToken =
        verificationResult && typeof verificationResult === "object" && "token" in verificationResult
          ? (verificationResult as { token?: string }).token
          : undefined;

      if (directToken) {
        saveToken(directToken);
      } else {
        const loginResult = await trpc.auth.login.mutate({
          email: formData.email.trim(),
          password: formData.password,
        });
        if (!("token" in loginResult) || !loginResult.token) {
          throw new Error("Unable to start your session. Please log in to continue.");
        }
        saveToken(loginResult.token);
      }

      clearSignupOtpState();
      const normalizedWebsiteUrl = formData.websiteUrl.trim();
      const normalizedEmail = formData.email.trim();
      if (normalizedWebsiteUrl && normalizedEmail) {
        writeSignupWebsitePrefill({
          email: normalizedEmail,
          websiteUrl: normalizedWebsiteUrl,
        });
      } else {
        clearSignupWebsitePrefill();
      }
      clearSignupBasicDetailsDraft();
      await refetchUser();
      successNotification({
        title: "Email verified",
        message: "Your account is ready. Complete your company profile to continue.",
      });
      navigate("/create-profile", { replace: true });
    } catch (err) {
      handleTrpcError(err);
    } finally {
      setVerifyingOtp(false);
    }
  };

  const handleResendOtp = async () => {
    if (otpCooldown > 0) return;
    setResendLoading(true);
    try {
      await trpc.auth.resendVerification.mutate({ email: formData.email.trim() });
      setOtpCooldown(60);
      successNotification({
        title: "Code sent",
        message: "A new verification code has been sent to your email.",
      });
    } catch (err) {
      handleTrpcError(err);
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <SignupFlowLayout>
      <Paper p="xl" radius="lg" maw={980} mx="auto" bg="#f7f9ff" style={{ border: `1px solid ${NAVY}` }}>

        <Title
          order={2}
          mb="md"
          ta="center"
          style={{
            color: NAVY,
            borderBottom: "1px solid #e8e8e8",
            paddingBottom: 10,
            fontWeight: 700,
          }}
        >
          {activeStep === 0 ? "Create your account" : "Verify your account"}
        </Title>
        <Box style={{ overflowX: "hidden", paddingBlock: 4 }}>
          <Box
            style={{
              display: "flex",
              width: "200%",
              transform: `translateX(-${activeStep * 50}%)`,
              transition: "transform 450ms cubic-bezier(0.22, 1, 0.36, 1)",
            }}
          >
            <Box style={{ width: "50%", paddingRight: isSmUp ? 20 : 0, boxSizing: "border-box" }}>
              <Text fz="lg" c="slate.6" mb="lg">
                Personal Details
              </Text>

              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" verticalSpacing="md" mb="md">
                <TextInput
                  label="Contact Person Name"
                  placeholder="Enter name"
                  required
                  size="md"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.currentTarget.value })}
                />
                <TextInput
                  label="Company Name"
                  placeholder="Enter your company name"
                  required
                  size="md"
                  value={formData.companyName}
                  onChange={(e) => setFormData({ ...formData, companyName: e.currentTarget.value })}
                  leftSection={<IconBuilding size={18} />}
                />
              </SimpleGrid>

              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" verticalSpacing="md" mb="md">
                <TextInput
                  label="Designation"
                  placeholder="Enter designation"
                  size="md"
                  value={formData.designation}
                  onChange={(e) => setFormData({ ...formData, designation: e.currentTarget.value })}
                  leftSection={<IconBriefcase size={18} />}
                />
                <TextInput
                  label="Company Email Address"
                  required
                  type="email"
                  placeholder="Enter email"
                  size="md"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.currentTarget.value })}
                  leftSection={<IconMail size={18} />}
                  error={
                    formData.email && !isValidEmail(formData.email)
                      ? "Please enter a valid email address"
                      : formData.email && isValidEmail(formData.email) && !isBusinessEmail(formData.email)
                        ? "Personal email addresses are not allowed. Please use your official business email."
                        : null
                  }
                />
              </SimpleGrid>

              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" verticalSpacing="md" mb="md">
                <TextInput
                  label="Company Website URL"
                  placeholder="https://yourcompany.com"
                  size="md"
                  value={formData.websiteUrl}
                  onChange={(e) => setFormData({ ...formData, websiteUrl: e.currentTarget.value })}
                  leftSection={<IconWorld size={18} />}
                  error={formData.websiteUrl && !isWebsiteValid ? "Please enter a valid website URL" : null}
                />
                <Box>
                  <Text fz="sm" fw={500} mb={5}>
                    Phone Number{" "}
                    <Text span c="red">
                      *
                    </Text>
                  </Text>
                  <PhoneInput
                    defaultCountry="in"
                    value={`${formData.phoneCountryCode}${formData.phoneNumber}`}
                    onChange={(phone, meta) => {
                      const countryCode = meta.country?.dialCode ? `+${meta.country.dialCode}` : "";
                      const nationalNumber = phone.replace(`+${meta.country?.dialCode || ""}`, "").trim();
                      setFormData((prev) => ({ ...prev, phoneCountryCode: countryCode, phoneNumber: nationalNumber }));
                    }}
                    style={{ width: "100%" }}
                    inputStyle={{
                      width: "100%",
                      height: "36px",
                      fontSize: "14px",
                      border: "1px solid var(--mantine-color-gray-3)",
                      borderLeft: "none",
                      borderRadius: "0 var(--mantine-radius-md) var(--mantine-radius-md) 0",
                      outline: "none",
                    }}
                    countrySelectorStyleProps={{
                      buttonStyle: {
                        height: "36px",
                        border: "1px solid var(--mantine-color-gray-3)",
                        borderRadius: "var(--mantine-radius-md) 0 0 var(--mantine-radius-md)",
                        padding: "0 12px",
                        backgroundColor: "white",
                      },
                      dropdownStyleProps: {
                        style: {
                          backgroundColor: "white",
                          boxShadow: "0 4px 12px #00000026",
                          border: "1px solid var(--mantine-color-gray-3)",
                          zIndex: 1000,
                        },
                      },
                    }}
                  />
                </Box>
              </SimpleGrid>

              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" verticalSpacing="md" mb="xl">
                <PasswordInput
                  label="Password"
                  placeholder="Enter password"
                  required
                  size="md"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.currentTarget.value })}
                  visible={showPassword}
                  onVisibilityChange={setShowPassword}
                  leftSection={<IconLock size={18} />}
                  error={formData.password.length > 0 && !isPasswordValid ? "Password must be at least 8 characters" : null}
                />

                <PasswordInput
                  label="Confirm Password"
                  placeholder="Re-enter password"
                  required
                  size="md"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.currentTarget.value })}
                  visible={showConfirmPassword}
                  onVisibilityChange={setShowConfirmPassword}
                  leftSection={<IconLock size={18} />}
                  error={formData.confirmPassword && !doPasswordsMatch ? "Passwords don't match" : null}
                />
              </SimpleGrid>

              <Checkbox
                size="xs"
                mb="lg"
                checked={formData.acceptedTermsAndPrivacy}
                onChange={(e) => setFormData({ ...formData, acceptedTermsAndPrivacy: e.currentTarget.checked })}
                label={
                  <Text component="span" fz="xs" c="gray.6" lh={1.45}>
                    By creating an account, I accept the{" "}
                    <Anchor
                      href={TERMS_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      c="gray.6"
                      fw={500}
                      fz="inherit"
                      underline="always"
                    >
                      Terms & Conditions
                    </Anchor>{" "}
                    and{" "}
                    <Anchor
                      href={PRIVACY_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      c="gray.6"
                      fw={500}
                      fz="inherit"
                      underline="always"
                    >
                      Privacy Policy
                    </Anchor>
                    .
                  </Text>
                }
              />

              <Flex justify="flex-end">
                <Button
                  size="lg"
                  px="xl"
                  onClick={handleStep1Submit}
                  loading={loading}
                  disabled={!isStep1Valid || loading}
                  loaderProps={{ color: "white" }}
                >
                  Continue
                </Button>
              </Flex>
            </Box>

            <Box
              style={{
                width: "50%",
                paddingLeft: isSmUp ? 20 : 0,
                boxSizing: "border-box",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Box w="100%" maw={440} mx="auto" px={{ base: "xs", sm: 0 }}>
                <Box mb="xl" ta="center">
                  <Text fz="sm" c="gray.6" lh={1.5} mb={6}>
                    We&apos;ve sent a 6-digit OTP to
                  </Text>
                  <Text
                    fz="sm"
                    fw={600}
                    c="dark.6"
                    lh={1.5}
                    style={{
                      display: "block",
                      overflowWrap: "break-word",
                      wordBreak: "normal",
                    }}
                  >
                    {formData.email.trim() || "your email"}
                  </Text>
                </Box>

                <Box w="100%" maw="100%" miw={0} mb="xl">
                  <PinInput
                    type="number"
                    length={6}
                    value={otpCode}
                    onChange={setOtpCode}
                    oneTimeCode
                    size={isSmUp ? "lg" : "sm"}
                    gap={isSmUp ? 10 : 6}
                    radius="md"
                    styles={{
                      root: {
                        width: "100%",
                        justifyContent: "center",
                        flexWrap: "nowrap",
                      },
                      pinInput: {
                        flex: "1 1 0",
                        minWidth: 0,
                      },
                    }}
                  />
                </Box>

                <Button
                  fullWidth
                  size="lg"
                  radius="md"
                  onClick={handleVerifyOtp}
                  loading={verifyingOtp}
                  disabled={otpCode.trim().length !== 6 || verifyingOtp}
                  loaderProps={{ color: "white" }}
                  styles={{
                    root: {
                      backgroundColor: NAVY,
                      "&:not(:disabled):hover": { backgroundColor: "#062d52" },
                    },
                  }}
                >
                  Verify & Continue
                </Button>

                <Text fz="sm" c="gray.6" ta="center" mt="lg" lh={1.6}>
                  Didn&apos;t receive OTP?{" "}
                  {otpCooldown > 0 ? (
                    <Text span c="dimmed" fz="inherit">
                      Resend OTP in {formatOtpCooldown(otpCooldown)}
                    </Text>
                  ) : resendLoading ? (
                    <Text span c="dimmed" fz="inherit">
                      Sending…
                    </Text>
                  ) : (
                    <Anchor
                      component="button"
                      type="button"
                      fz="inherit"
                      c="#2563eb"
                      underline="always"
                      onClick={handleResendOtp}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        padding: 0,
                        font: "inherit",
                      }}
                    >
                      Resend OTP
                    </Anchor>
                  )}
                </Text>

                <Text ta="center" mt="md">
                  <Anchor
                    component="button"
                    type="button"
                    fz="sm"
                    c="gray.6"
                    underline="hover"
                    onClick={() => {
                      setActiveStep(0);
                      clearSignupOtpState();
                    }}
                    style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}
                  >
                    Edit details
                  </Anchor>
                </Text>
              </Box>
            </Box>
          </Box>
        </Box>
      </Paper>
    </SignupFlowLayout>
  );
}

export default SignupPage;
